/** Read dropped directory entries before their drag data store is released. */
export async function droppedImportFiles(transfer: DataTransfer): Promise<File[]> {
  const entries = Array.from(transfer.items).filter((item) => item.kind === "file")
    .map((item) => ({ entry: item.webkitGetAsEntry?.() ?? null, file: item.getAsFile() }));
  const fallback = Array.from(transfer.files);
  const files: File[] = [];
  let bytes = 0;
  let visited = 0;
  function add(file: File) {
    if (file.size === 0) throw new Error(`${file.name}: Die Datei ist leer oder der Ordner konnte nicht gelesen werden. Bitte den Ordner als ZIP hinzufügen.`);
    bytes += file.size;
    files.push(file);
    if (files.length > 200 || bytes > 200 * 1024 * 1024) throw new Error("Maximal 200 Dateien und 200 MiB pro Auswahl.");
  }
  async function visit(entry: FileSystemEntry, depth: number): Promise<void> {
    if (++visited > 1000 || depth > 20) throw new Error("Der Ordner enthält zu viele Unterordner.");
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
      add(file);
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      // Chromium returns directory entries in chunks; one call is insufficient.
      while (true) {
        const children = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
        if (!children.length) break;
        for (const child of children) await visit(child, depth + 1);
      }
    }
  }
  if (entries.length) {
    for (const { entry, file } of entries) {
      if (entry) await visit(entry, 0);
      else if (file) add(file);
    }
  } else fallback.forEach(add);
  if (!files.length) throw new Error("Keine Dateien gefunden. Bitte einen Ordner mit Dateien oder eine ZIP-Datei auswählen.");
  return files;
}

export function importFileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KiB`
    : `${(bytes / 1024 / 1024).toLocaleString("de-AT", { maximumFractionDigits: 1 })} MiB`;
}

const IMPORT_DOC_TYPE_LABELS: Record<string, string> = {
  rechnung: "Rechnung",
  einzelauftrag: "Einzelauftrag",
  hauptabrufer: "Hauptabruf",
  angebot: "Angebot",
  gutschrift: "Gutschrift",
  retoure: "Retoure",
};

export function importDocTypeLabel(docType: string): string {
  return IMPORT_DOC_TYPE_LABELS[docType] ?? docType;
}

/** Only the last path segment, without characters that could pass for markup. */
export function safeDisplayName(value: unknown): string {
  if (typeof value !== "string") return "";
  const cleaned = value.split(/[\\/]/).filter(Boolean).pop() ?? "";
  return cleaned.replace(/[^\w.\-:]/g, " ").trim();
}
